<template>
    <AppLayout
        ref="appLayout"
        page="/dashboard"
        pageTitle="Time Clock"
        backLink="/dashboard/reports"
        hasFab
        @fabAction="showTimeClockForm = true"
    >
        <v-col cols="12" class="pa-1">
            <Stats class="pb-3" :stats="stats" />
            <v-card class="soli-round">
                <v-container class="px-3" fluid>
                    <ReportHeader
                        filter
                        :legacyDate="payDay"
                        :dateRange="dateRange"
                        :rangeOptions="{
                            interval:
                                $page.props.auth.user.selected_location[
                                    'pay_int'
                                ],
                            frequency:
                                $page.props.auth.user.selected_location[
                                    'pay_freq'
                                ],
                        }"
                        subtitle="Time clock report by Pay Period"
                        reportUrl="/dashboard/reports/time-clock/"
                        @search="search = $event"
                    >
                        <template #extendedSubtitle>
                            <v-list-item-subtitle
                                v-if="isProcessed"
                                class="font-bold error--text"
                                style="white-space: normal"
                                ><v-icon left small color="error">flag</v-icon
                                >{{
                                    `Processed on ${globalMomentFullDateAtTime(
                                        processed_at
                                    )}`
                                }}</v-list-item-subtitle
                            >
                        </template>
                        <template #filterItem>
                            <v-list dense>
                                <v-list-item class="flex justify-center pt-1">
                                    <v-switch
                                        dense
                                        class="mt-0"
                                        @change="
                                            groupByStaff
                                                ? (groupByStaff = null)
                                                : (groupByStaff = 'staff_id')
                                        "
                                        :label="`Group by Employee`"
                                        hide-details
                                /></v-list-item>
                            </v-list>
                        </template>
                    </ReportHeader>
                    <v-data-table
                        ref="table"
                        :headers="headers"
                        :items="report"
                        :search="search"
                        hide-default-footer
                        disable-pagination
                        mobile-breakpoint="0"
                        item-key="time_clock_id"
                        class="cursor-pointer table-nowrap"
                        :group-by="groupByStaff"
                        @click:row="editEntry($event)"
                    >
                        <template
                            v-slot:[`group.header`]="{ items, isOpen, toggle }"
                        >
                            <th :colspan="headers.length">
                                <div class="flex font-weight-regular">
                                    <div
                                        class="flex-col align-self-center mr-2"
                                    >
                                        <v-icon @click="toggle"
                                            >{{ isOpen ? "remove" : "add" }}
                                        </v-icon>
                                    </div>
                                    <SoliListAvatar
                                        :avatar="
                                            items[0].user.profile_photo_url
                                        "
                                        :title="items[0].user.name"
                                        :subtitle="items[0].user.email"
                                        :href="`/profile/${items[0].user.id}/manage`"
                                    />
                                </div>
                            </th>
                        </template>
                        <template v-slot:[`group.summary`]="{ items }">
                            <div :colspan="headers.length">
                                <v-list>
                                    <v-list-item>
                                        <v-list-item-subtitle
                                            class="font-weight-black soli-grey"
                                        >
                                            {{
                                                `Total Hours: ${items
                                                    .map((e) =>
                                                        Number(e.duration)
                                                    )
                                                    .reduce((a, b) => a + b)}`
                                            }}</v-list-item-subtitle
                                        >
                                    </v-list-item>
                                </v-list>
                            </div>
                        </template>
                        <template v-slot:[`item.user.name`]="{ item }">
                            <SoliListAvatar
                                v-if="!groupByStaff"
                                :avatar="item.user.profile_photo_url"
                                :title="item.user.name"
                                :subtitle="item.user.email"
                                :href="`/profile/${item.staff_id}/manage`"
                            />
                        </template>
                        <template v-slot:[`item.starts_at`]="{ item }">
                            <div class="soli-grey">
                                {{ globalMomentFullDateAtTime(item.starts_at) }}
                            </div>
                        </template>
                        <template v-slot:[`item.ends_at`]="{ item }">
                            <div class="soli-grey">
                                {{
                                    item.ends_at
                                        ? globalMomentFullDateAtTime(
                                              item.ends_at
                                          )
                                        : null
                                }}
                            </div>
                        </template>
                        <template v-slot:[`item.rate_amount`]="{ item }">
                            {{ `${formatCurrency(item.rate_amount)}/hour` }}
                        </template>
                    </v-data-table>
                </v-container>
            </v-card>
        </v-col>

        <SoliForm
            v-if="showTimeClockForm"
            :title="`${form.id ? 'Edit' : 'New'} Time Clock Entry`"
            icon="save"
            ref="form"
            persistent
            :loadingProp="form.processing"
            text="Save"
            @input="valid = $event"
            :saveChanges="valid"
            @submit="submitTimeClock()"
            @reset="resetTimeClock()"
        >
            <template #body>
                <div class="flex flex-col gap-4">
                    <SoliStaff
                        v-model="form.user"
                        :disabled="editing"
                        :rules="rules.select"
                        autocomplete
                        rateOnly
                        hide-details="auto"
                    />
                    <StaffPayRatesSelect
                        v-if="form.user"
                        v-model="form.role"
                        :key="form.user.id"
                        :end="
                            moment(
                                new Date(`${form.endDate} ${form.endTime}`)
                            ).toISOString()
                        "
                        :user="form.user"
                        :rules="rules.select"
                    />
                    <SoliDate
                        v-model="form.startDate"
                        label="Start Date"
                        noLimit
                        :min="
                            editing
                                ? new Date(dateRange[0]).toISOString()
                                : null
                        "
                        :error="errors.starts_at"
                        :rules="rules.input"
                        @input="form.endDate = form.startDate"
                        hide-details="auto"
                    />
                    <SoliTime
                        v-model="form.startTime"
                        :error="errors.starts_at"
                        :rules="rules.input"
                        allowAll
                        hide-details="auto"
                    />
                    <SoliDate
                        v-model="form.endDate"
                        label="End Date"
                        noLimit
                        :error="errors.ends_at"
                        :rules="form.endTime ? rules.input : []"
                        :min="new Date(form.startDate).toISOString()"
                        :max="
                            editing
                                ? new Date(dateRange[1]).toISOString()
                                : null
                        "
                        hide-details="auto"
                    />
                    <SoliTime
                        v-model="form.endTime"
                        label="End Time"
                        icon="timer_off"
                        :error="errors.ends_at"
                        :rules="endRule"
                        allowAll
                        hide-details="auto"
                    />
                </div>
            </template>
            <template v-slot:cancelBtn="attrs">
                <SoliBtn
                    v-if="editing"
                    :text="'Delete'"
                    :icon="'delete'"
                    v-bind="attrs"
                    class="mt-3"
                    color="soli-status-red"
                    @click="confirmDelete = true"
                />
            </template>
        </SoliForm>

        <SoliChoice
            v-if="confirmDelete"
            title="Are you sure?"
            :subtitle="`This will delete the following entry for ${form.user.name}`"
            button1="Delete"
            button2="Nevermind"
            @option1="submitTimeClock(true)"
            @option2="closeModal(() => (confirmDelete = false))"
        />
    </AppLayout>
</template>

<script>
import AppLayout from "./../../Layouts/AppLayout";
import ReportHeader from "../../Components/Reports/ReportHeader.vue";
import Stats from "../../Components/Reports/Stats.vue";
import globalMoment from "../../Mixins/GlobalMoment";
import SoliForm from "./../../Components/Base/SoliForm.vue";
import StaffPayRatesSelect from "../../Components/Staff/StaffPayRatesSelect.vue";

export default {
    mixins: [globalMoment],
    components: {
        AppLayout,
        ReportHeader,
        Stats,
        SoliForm,
        StaffPayRatesSelect,
    },
    props: {
        report: {
            type: Array,
            default: () => [],
        },
        dateRange: {
            type: Array,
            default: () => [],
        },
        payDay: {
            type: String,
            default: "",
        },
        processed_at: {
            type: String,
            default: "",
        },
        total: {
            type: Number,
            default: 0,
        },
        errors: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            search: "",
            groupByStaff: null,
            form: this.$inertia.form({
                id: null,
                user: null,
                startDate: null,
                endDate: null,
                startTime: null,
                endTime: null,
                role: null,
            }),
            valid: false,
            showTimeClockForm: false,
            confirmDelete: false,
            rules: SoliForm.formRules,
            endRule: [
                (val) => {
                    if (
                        !this.form.startDate ||
                        !this.form.endDate ||
                        !this.form.startTime
                    ) {
                        return true;
                    }
                    if (!this.form.endTime || !val) return true;
                    return (
                        new Date(
                            this.form.startDate +
                                " " +
                                this.globalMomentGetTime(val)
                        ).toISOString() >=
                            new Date(
                                this.form.endDate +
                                    " " +
                                    this.globalMomentGetTime(
                                        this.form.startTime
                                    )
                            ).toISOString() ||
                        "End time must be greater than start time"
                    );
                },
            ],
        };
    },
    methods: {
        editEntry(event) {
            console.log(event.time_clock_id);
            this.showTimeClockForm = true;
            this.form.id = event.time_clock_id;
            this.form.location_user_id = event.rate_location_user_id;
            this.form.user = { id: event.staff_id, name: event.name };
            this.form.role = { id: event.rate_role_id, name: event.role_name };
            this.form.startDate = moment(new Date(event.starts_at)).format(
                "MM/D/Y"
            );
            this.form.endDate = event.ends_at
                ? moment(new Date(event.ends_at)).format("MM/D/Y")
                : null;
            this.form.startTime = new Date(event.starts_at).toLocaleTimeString(
                "en-US",
                {
                    hour: "2-digit",
                    minute: "2-digit",
                }
            );
            this.form.endTime = event.ends_at
                ? new Date(event.ends_at).toLocaleTimeString("en-US", {
                      hour: "2-digit",
                      minute: "2-digit",
                  })
                : null;

            /*this.roleOptions = [
                "Teacher",
                "Staff",
                "Assistant Community Manager",
                "Community Manager",
                "Area Manager",
            ].filter((e) => this.user.hasRoles.includes(e));*/
        },
        submitTimeClock(deleting = false) {
            if (!this.$refs.form.$refs.form.validate()) return;

            this.form.transform((form) => ({
                user_id: form.user.id,
                starts_at: new Date(form.startDate + " " + form.startTime),
                ends_at: form.endTime
                    ? new Date(form.endDate + " " + form.endTime)
                    : null,
                role_id: form.role.id,
                realTime: 0,
            }));

            const options = {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    this.$refs.appLayout.showMessage = true;
                    this.$refs.form.reset();
                },
                onError: (errors) => {
                    console.log(
                        "Form returned errors: " + JSON.stringify(errors)
                    );
                },
            };

            if (deleting) {
                return this.form.delete(
                    `/time-clocks/${this.form.id}`,
                    options
                );
            }

            if (this.editing) {
                return this.form.put(`/time-clocks/${this.form.id}`, options);
            }

            return this.form.post("/time-clocks", options);
        },
        resetTimeClock() {
            this.form.defaults({
                id: null,
                user: null,
                startDate: null,
                endDate: null,
                startTime: null,
                endTime: null,
                role_id: null,
            });
            this.form.reset();
            this.showTimeClockForm = false;
            this.confirmDelete = false;
        },
    },
    computed: {
        isProcessed() {
            return moment(new Date(this.processed_at)).isBefore(moment());
        },
        stats() {
            return [
                {
                    title: "Pay Date",
                    data: this.globalMomentDateShort(this.payDay),
                    color: "accent",
                    icon: "calendar_today",
                },
                {
                    title: "Total Hours",
                    data: this.total,
                    color: "soli-status-green",
                    icon: "timer",
                },
                {
                    title: "People",
                    data: [...new Set(this.report.map((e) => e.staff_id))]
                        .length,
                    color: "secondary",
                    icon: "people",
                },
            ];
        },
        editing() {
            return !!this.form.id;
        },
        headers() {
            return [
                {
                    text: "Employee",
                    value: "user.name",
                },
                {
                    text: "Time In",
                    value: "starts_at",
                },
                {
                    text: "Time Out",
                    value: "ends_at",
                },
                {
                    text: "Duration",
                    value: "duration",
                },
                {
                    text: "Role",
                    value: "role_name",
                },
                {
                    text: "Rate",
                    value: "rate_amount",
                },
            ];
        },
    },
};
</script>

<style></style>
